/**
 * País de la petición (ISO 3166-1 alpha-2, MAYÚSCULAS).
 *
 * Tipo marcado a propósito: en este dominio conviven `language`/`locale` en
 * minúsculas (`'es'`, `'en'`, `'pt'`) con países en mayúsculas (`'ES'`, `'MX'`),
 * y son cosas distintas — un padre con el móvil en `es-MX` habla español pero
 * compra en México. La marca hace que pasar un idioma donde se espera un país
 * (o al revés) no compile, en vez de fallar silenciosamente en runtime.
 *
 * `null` significa "país desconocido", no "sin país": es un estado real
 * (peticiones locales, proxies que el GeoIP no resuelve) y cada política decide
 * qué hacer con él.
 */
declare const countryCodeBrand: unique symbol

export type CountryCode = string & { readonly [countryCodeBrand]: true }

const COUNTRY_CODE_PATTERN = /^[A-Z]{2}$/

/**
 * `XX` no es un país: es el marcador de "no resuelto" que devuelven los GeoIP
 * (Vercel incluido) cuando la IP no se puede geolocalizar. Se colapsa a
 * desconocido para que las políticas no tengan que conocer el detalle del
 * proveedor.
 */
const UNRESOLVED_COUNTRY = 'XX'

/**
 * Normaliza a `CountryCode` o devuelve `null`. Acepta minúsculas y espacios
 * porque las fuentes son heterogéneas (headers, envs escritas a mano).
 *
 * Valida la FORMA (dos letras), no la pertenencia al registro ISO: `'ZZ'` pasa.
 * Es deliberado — mantener aquí la lista de 249 países sería un fichero que
 * envejece — y es inocuo: un código inexistente en la allowlist no casa con
 * ningún país real, y del header sólo llegan códigos que el GeoIP emite.
 */
export function parseCountryCode(value: unknown): CountryCode | null {
  if (typeof value !== 'string') return null

  const normalized = value.trim().toUpperCase()
  if (!COUNTRY_CODE_PATTERN.test(normalized)) return null
  if (normalized === UNRESOLVED_COUNTRY) return null

  return normalized as CountryCode
}

/**
 * ¿Este país pasa una allowlist? Convención compartida por todos los gates
 * geográficos:
 *
 *  - **lista vacía ⇒ sin restricción**, para que la ausencia de configuración
 *    sea exactamente el comportamiento previo a existir el gate (y por tanto
 *    apagarlo sea borrar la env, no tocar código);
 *  - **país desconocido con lista activa ⇒ fuera** (fail-closed), en la misma
 *    línea que el resto de gates del sistema (feature flags, trial): ante la
 *    duda no se regala.
 */
export function isCountryAllowed(
  country: CountryCode | null,
  allowlist: readonly CountryCode[]
): boolean {
  if (allowlist.length === 0) return true

  return country !== null && allowlist.includes(country)
}

/**
 * ¿Este país queda EXCLUIDO por una denylist? Convención hermana de
 * `isCountryAllowed` para los gates que se expresan al revés — «esto se ofrece
 * en todas partes MENOS aquí»:
 *
 *  - **lista vacía ⇒ no se excluye a nadie**, igual que la allowlist vacía: la
 *    ausencia de configuración es el comportamiento previo al gate. Aquí no
 *    hace falta guarda —una lista vacía no contiene a nadie—, al contrario que
 *    en `isCountryAllowed`, donde el caso vacío sí invierte el resultado;
 *  - **país desconocido ⇒ NO excluido** (fail-open), al contrario que la
 *    allowlist. La asimetría es deliberada y sale de qué se está gateando: la
 *    allowlist protege un REGALO (ante la duda no se regala), la denylist
 *    protege un embudo de VENTA (ante la duda no se pierde la venta, que además
 *    es lo único que el usuario desconocido vería del pack).
 */
export function isCountryDenied(
  country: CountryCode | null,
  denylist: readonly CountryCode[]
): boolean {
  return country !== null && denylist.includes(country)
}

/**
 * Parsea una lista separada por comas (formato de las envs de allowlist).
 * Descarta entradas no válidas en silencio en vez de tumbar el arranque: una
 * env mal escrita no debe dejar el servicio caído, y el consumidor ya trata la
 * lista vacía como "sin restricción".
 */
export function parseCountryCodes(value: unknown): readonly CountryCode[] {
  if (typeof value !== 'string') return []

  const parsed = value
    .split(',')
    .map(parseCountryCode)
    .filter((country): country is CountryCode => country !== null)

  return [...new Set(parsed)]
}
